import { h, Component, Fragment } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer } from 'preact-i18n';

import localUtil from '../../lib/util';
import DateTile from '../../components/gui/dateTile';
import { appConfirm } from '../../lib/dialogs';
import { beginDialogAction } from '../../lib/dialogAction';

@observer
class DateSelector extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            expandedDates: {},
        };
    }

    // Weekday + time range for a date row; the DateTile next to it carries
    // day, month and year.
    renderTimeLine = date => {
        if (!date.startTime) {
            return '';
        }
        const weekday = util.formatDate(date.startTime, { weekday: 'long', locale: 'nb-NO' }, true);
        const start = util.formatDate(date.startTime, { hour: '2-digit', minute: '2-digit', locale: 'nb-NO' }, true);
        const end = date.endTime ? util.formatDate(date.endTime, { hour: '2-digit', minute: '2-digit', locale: 'nb-NO' }, true) : '';
        return (
            <div class='text-body-secondary small'>
                <i class='fa-solid fa-clock me-1' />
                {weekday} · {start}{end ? ` - ${end}` : ''}
            </div>
        );
    }

    reloadCourse = async () => {
        const { courseStore } = this.props.stores;
        const { course } = courseStore;

        await courseStore.load(course.id, false, {
            addData: ['tags'],
            query: {
                hostname: courseStore.currentHostname,
            },
        });
    }

    getErrorMessage(errorCode, response) {
        const errorMessages = {
            DATE_FULL: 'Beklager, datoen er nå full. Listen oppdateres.',
            ALREADY_SIGNED_UP: 'Du er allerede påmeldt denne datoen.',
            REGISTRATION_CLOSED: 'Registreringen er stengt.',
            REGISTRATION_NOT_STARTED: 'Registreringen har ikke startet enda.',
            REGISTRATION_ENDED: 'Registreringen er avsluttet.',
            DATE_NOT_FOUND: 'Kursdato ikke funnet.',
            UNAUTHORIZED: 'Du må være logget inn for å melde deg på.',
            DATE_PASSED: 'Kan ikke avmelde etter at kursdatoen har passert.',
            NOT_SIGNED_UP: 'Du er ikke påmeldt denne datoen.',
        };
        return errorMessages[errorCode] || response?.message || 'Kunne ikke melde på. Prøv igjen.';
    }

    handleSignUp = async (e) => {
        const { dateMd5 } = e.target.closest('button').dataset;
        const { courseStore, userStore, appState } = this.props.stores;
        const { user = {} } = userStore;
        const { callback } = this.props;

        if (!user || !user.id) {
            appState.addToast({
                type: 'error',
                message: 'Du må være logget inn for å melde deg på.',
            }, 5000);
            return;
        }

        this.setState({ loading: dateMd5 });

        try {
            const response = await courseStore.signUpForDate(dateMd5);

            if (response && response.status === 200) {
                appState.addToast({
                    type: 'success',
                    message: 'Du er nå påmeldt kurset!',
                }, 5000);

                await this.reloadCourse();

                if (callback) {
                    await callback();
                }
            } else if (response && response.errorCode) {
                const errorMessage = this.getErrorMessage(response.errorCode, response);
                appState.addToast({
                    type: 'error',
                    message: errorMessage,
                }, 5000);

                if (response.errorCode === 'DATE_FULL' || response.errorCode === 'ALREADY_SIGNED_UP') {
                    await this.reloadCourse();
                }
            } else if (response) {
                appState.addToast({
                    type: 'error',
                    message: `Kunne ikke melde på: ${response?.message || 'Ukjent feil'}`,
                }, 5000);
            }
        } catch (err) {
            appState.addToast({
                type: 'error',
                message: `Kunne ikke melde på: ${err.message}`,
            }, 5000);
        } finally {
            this.setState({ loading: false });
        }
    }

    handleCancel = async (e) => {
        const dialogAction = beginDialogAction(this, 'handleCancel', () => [
            this.props.stores.courseStore.course?.id,
        ]);
        if (!dialogAction) return false;
        try {
            const { dateMd5 } = e.target.closest('button').dataset;
            const { courseStore, userStore, appState } = this.props.stores;
            const { user = {} } = userStore;
            const { callback } = this.props;

            if (!((await appConfirm('Er du sikker på at du vil melde deg av denne datoen?')) && dialogAction.isCurrent())) {
                return;
            }

            this.setState({ loading: dateMd5 });

            try {
                const response = await courseStore.cancelSignup(dateMd5);

                if (response && response.status === 200) {
                    appState.addToast({
                        type: 'success',
                        message: 'Du er nå avmeldt kurset.',
                    }, 5000);

                    await this.reloadCourse();

                    if (callback) {
                        await callback();
                    }
                } else if (response && response.errorCode) {
                    const errorMessage = this.getErrorMessage(response.errorCode, response);
                    appState.addToast({
                        type: 'error',
                        message: errorMessage,
                    }, 5000);
                } else if (response) {
                    appState.addToast({
                        type: 'error',
                        message: `Kunne ikke melde av: ${response?.message || 'Ukjent feil'}`,
                    }, 5000);
                }
            } catch (err) {
                appState.addToast({
                    type: 'error',
                    message: `Kunne ikke melde av: ${err.message}`,
                }, 5000);
            } finally {
                this.setState({ loading: false });
            }
        } finally {
            dialogAction.finish();
        }
    }

    toggleDateDetails = (dateMd5) => {
        this.setState(prevState => ({
            expandedDates: {
                ...prevState.expandedDates,
                [dateMd5]: !prevState.expandedDates[dateMd5],
            },
        }));
    }

    renderScheduleOnly() {
        const { expandedDates } = this.state;
        const { course } = this.props;

        const dates = course.dates || [];

        if (!dates || dates.length === 0) {
            return (
                <div class='text-center text-muted py-4'>
                    <i class='fa-duotone fa-calendar-xmark fa-3x mb-3' />
                    <p><Text id='course.noDatesAvailable'>Ingen datoer tilgjengelig</Text></p>
                </div>
            );
        }

        return (
            <div class='list-group'>
                {dates.map((date, index) => {
                    const isExpanded = expandedDates[date.md5];
                    const isPast = date.startTime && new Date(date.startTime) < new Date();

                    return (
                        <div
                            key={date.md5}
                            class={`list-group-item ${isPast ? 'list-group-item-secondary' : ''}`}
                        >
                            <div
                                class='d-flex align-items-center'
                                onClick={() => date.description && this.toggleDateDetails(date.md5)}
                                style={date.description ? 'cursor: pointer;' : ''}
                            >
                                <DateTile date={date.startTime} isCancelled={!!isPast} />
                                <div class='flex-grow-1 ms-3 overflow-hidden'>
                                    <div class='d-flex align-items-center mb-1'>
                                        {date.title ? (
                                            <strong>{date.title}</strong>
                                        ) : (
                                            <strong>Dag {index + 1}</strong>
                                        )}
                                        {isPast && (
                                            <span class='badge text-bg-secondary ms-2'>
                                                <i class='fa-solid fa-check me-1' />
                                                Gjennomført
                                            </span>
                                        )}
                                    </div>
                                    {this.renderTimeLine(date)}
                                    {date.location && (
                                        <div class='text-body-secondary small mt-1'>
                                            <i class='fa-solid fa-location-dot me-1' />
                                            {date.location}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Expanded details */}
                            {isExpanded && date.description && (
                                <div class='mt-3 pt-3 border-top'>
                                    <p class='mb-0 small'>{date.description}</p>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    }

    render() {
        const { loading, expandedDates } = this.state;
        const { course, scheduleOnly } = this.props;
        const { courseStore, userStore } = this.props.stores;
        const { user = {} } = userStore;

        // If scheduleOnly mode, render simplified schedule view
        if (scheduleOnly) {
            return this.renderScheduleOnly();
        }

        const availableDates = courseStore.getAvailableDatesForUser(user.id);

        if (!availableDates || availableDates.length === 0) {
            return (
                <div class='text-center text-muted py-4'>
                    <i class='fa-duotone fa-calendar-xmark fa-3x mb-3' />
                    <p><Text id='course.noDatesAvailable'>Ingen datoer tilgjengelig</Text></p>
                </div>
            );
        }

        return (<>
            <div class='list-group'>
                {availableDates.map((date, index) => {
                    const isLoading = loading === date.md5;
                    const isExpanded = expandedDates[date.md5];
                    const isPast = date.startTime && new Date(date.startTime) < new Date();

                    return (
                        <div
                            key={date.md5}
                            class={`list-group-item ${date.isUserSignedUp ? 'list-group-item-success' : ''} ${date.isFull ? 'list-group-item-secondary' : ''}`}
                        >
                            <div class='d-flex justify-content-between align-items-start'>
                                <div class='flex-grow-1 d-flex align-items-center overflow-hidden' onClick={() => this.toggleDateDetails(date.md5)} style='cursor: pointer;'>
                                <DateTile date={date.startTime} isCancelled={!!isPast} />
                                <div class='flex-grow-1 ms-3 overflow-hidden'>
                                    <div class='d-flex align-items-center mb-1'>
                                        {date.title ? (
                                            <strong>{date.title}</strong>
                                        ) : (
                                            <strong>Dato {index + 1}</strong>
                                        )}
                                        {date.isUserSignedUp && (
                                            <span class='badge text-bg-success ms-2'>
                                                <i class='fa-solid fa-check me-1' />
                                                Påmeldt
                                            </span>
                                        )}
                                        {date.isFull && !date.isUserSignedUp && (
                                            <span class='badge text-bg-danger ms-2'>
                                                <i class='fa-solid fa-ban me-1' />
                                                Full
                                            </span>
                                        )}
                                        {isPast && (
                                            <span class='badge text-bg-secondary ms-2'>
                                                <i class='fa-solid fa-clock me-1' />
                                                Passert
                                            </span>
                                        )}
                                    </div>
                                    {this.renderTimeLine(date)}
                                    {date.slotsTotal > 0 && (
                                        <div class='text-body-secondary small mt-1'>
                                            <i class='fa-solid fa-users me-1' />
                                            {date.slotsUsed} / {date.slotsTotal} plasser
                                            {date.slotsTotal - date.slotsUsed > 0 && !date.isFull && (
                                                <span class='text-success ms-2'>
                                                    ({date.slotsTotal - date.slotsUsed} ledige)
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    {date.location && (
                                        <div class='text-body-secondary small mt-1'>
                                            <i class='fa-solid fa-location-dot me-1' />
                                            {date.location}
                                        </div>
                                    )}
                                    {date.price && date.price !== course.price && (
                                        <div class='text-body-secondary small mt-1'>
                                            <i class='fa-solid fa-coins me-1' />
                                            {util.format(date.price, 0)} kr (inkl. mva)
                                        </div>
                                    )}
                                </div>
                                </div>

                                <div class='ms-3'>
                                    {date.isUserSignedUp ? (
                                        <button
                                            class='btn btn-outline-danger btn-sm rounded-pill'
                                            onClick={this.handleCancel}
                                            data-date-md5={date.md5}
                                            disabled={isLoading || isPast}
                                        >
                                            {isLoading ? (
                                                <span class='spinner-border spinner-border-sm' role='status'>
                                                    <span class='visually-hidden'>Loading...</span>
                                                </span>
                                            ) : (
                                                <>
                                                    <i class='fa-solid fa-xmark me-1' />
                                                    <Text id='course.cancel'>Avmeld</Text>
                                                </>
                                            )}
                                        </button>
                                    ) : date.canSignUp && !isPast ? (
                                        <button
                                            class='btn btn-primary btn-sm rounded-pill'
                                            onClick={this.handleSignUp}
                                            data-date-md5={date.md5}
                                            disabled={isLoading}
                                        >
                                            {isLoading ? (
                                                <span class='spinner-border spinner-border-sm' role='status'>
                                                    <span class='visually-hidden'>Loading...</span>
                                                </span>
                                            ) : (
                                                <>
                                                    <i class='fa-solid fa-plus me-1' />
                                                    <Text id='course.signup'>Meld på</Text>
                                                </>
                                            )}
                                        </button>
                                    ) : (
                                        <button class='btn btn-secondary btn-sm rounded-pill' disabled>
                                            {date.isFull ? (
                                                <Text id='course.full'>Full</Text>
                                            ) : isPast ? (
                                                <Text id='course.passed'>Passert</Text>
                                            ) : (
                                                <Text id='course.unavailable'>Utilgjengelig</Text>
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Expanded details */}
                            {isExpanded && date.description && (
                                <div class='mt-3 pt-3 border-top'>
                                    <p class='mb-0 small'>{date.description}</p>
                                </div>
                            )}

                            {/* Sale info if signed up */}
                            {date.isUserSignedUp && date.userSignup?.saleId && (
                                <div class='mt-2 pt-2 border-top'>
                                    <small class='text-body-secondary'>
                                        <i class='fa-solid fa-receipt me-1' />
                                        Salg #{date.userSignup.saleNumber || date.userSignup.saleId}
                                    </small>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Legend */}
            <div class='mt-3 d-flex flex-wrap gap-3 text-body-secondary small'>
                <span><span class='badge text-bg-success me-1'>Påmeldt</span> Du er påmeldt</span>
                <span><span class='badge text-bg-danger me-1'>Full</span> Ingen ledige plasser</span>
                <span><span class='badge text-bg-secondary me-1'>Passert</span> Datoen er passert</span>
            </div>
        </>);
    }
}

export default DateSelector;
